import React, { PureComponent } from 'react';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import { withWizard } from '@cimpress-technology/ff-react-components/lib/Wizard';
import StepTitle from '../../../../common/StepTitle';
import WizardNavigator from '../../../../common/WizardNavigator';
import { css } from 'react-emotion';
import { Accordion } from '@cimpress/react-components';
import SkuCard from '../../shared/SkuCard/SkuCard';
import Button from '../../../../common/Button';
import { evaluateCondition } from '../../../../../utilities/conditions';
import SkuCardVirtualizedList from '../../SetupPricingTermStep/SkuCardVirtualizedList';
import isEmpty from 'lodash/isEmpty';
import { ALL, INDIVIDUAL } from '../../SetupShippingPricingTermStep/ShippingTermCard';
import {
  createFlowIsShippingPriceTermType,
  TermTypeLabel,
  getCreateFlowAgreementTypeDisplay
} from '../../../../../constants/termTypes';

const section = css`
  margin-bottom: 20px;
`;

const sectionHeaderStyle = css({
  marginBottom: '30px',
  textAlign: 'center',
  fontSize: '20px'
});

const tableHeaderStyle = css({
  borderBottom: '1px solid #E8E8E8',
  margin: '0 0 10px'
});

const dlStyle = css({
  marginBottom: '0'
});

const tableSpacing = css({
  marginBottom: '20px'
});

class ReviewAndFinalizeStep extends PureComponent {
  static defaultProps = {};

  static propTypes = {};

  state = {
    success: false
  };

  onNext = () => {
    const { wizard } = this.props;
    wizard.setStep(wizard.next);
  };

  skuCardRenderer = skuInfo => <SkuCard skuInfo={skuInfo} />;

  getDestinationCountryDescription = shippingPricingTerm => {
    let title;
    let description;
    const { t } = this.props;
    switch (shippingPricingTerm.selectedCountryType) {
      case ALL:
        title = t('contractInfo:destinationCountriesTitle');
        description = t('contractInfo:allCountries');
        break;
      case INDIVIDUAL:
        title = t('contractInfo:destinationCountriesTitle');
        description = shippingPricingTerm.selectedCountries.join(', ');
        break;
      default:
        title = t('contractInfo:excludedCountriesTitle');
        description = shippingPricingTerm.selectedCountries.join(', ');
        break;
    }

    return (
      <div>
        <dt>{title}</dt>
        <dd>{description}</dd>
      </div>
    );
  };

  render() {
    const {
      t,
      flow: {
        initiator,
        acceptors,
        productPricingTerm,
        shippingPricingTerm,
        selectedSkus,
        shouldHaveGracePeriod,
        gracePeriodApprovalDays
      }
    } = this.props;

    const selectedSkuCards = (
      <SkuCardVirtualizedList
        skuCardRenderer={this.skuCardRenderer}
        skuCardList={Object.values(selectedSkus)}
        height={400}
      />
    );

    const pricingTermsTable = !isEmpty(productPricingTerm) &&
      productPricingTerm.termType && (
        <div className={tableSpacing}>
          <header className={tableHeaderStyle}>
            <h5>{t('createContract:pricingContract.title')}</h5>
          </header>
          <div>
            <dt>{t('common:commonTerminology.name')}</dt>
            <dd>{productPricingTerm.termName}</dd>
          </div>
          <div>
            <dt>{t('common:commonTerminology.type')}</dt>
            <dd>{`${t(productPricingTerm.termType)} ${getCreateFlowAgreementTypeDisplay(productPricingTerm)}`}</dd>
          </div>
          {productPricingTerm.termType === TermTypeLabel.SPECIAL_PRICE && (
            <div>
              <dt>{t('common:commonTerminology.priceModel')}</dt>
              <dd>{productPricingTerm.priceModel.name}</dd>
            </div>
          )}
        </div>
      );

    const shippingTermsTable = !isEmpty(shippingPricingTerm) &&
      shippingPricingTerm.termType && (
        <div>
          <header className={tableHeaderStyle}>
            <h5>{t('createContract:shippingContract.title')}</h5>
          </header>
          <div>
            <dt>{t('common:commonTerminology.name')}</dt>
            <dd>{shippingPricingTerm.termName}</dd>
          </div>
          <div>
            <dt>{t('common:commonTerminology.type')}</dt>
            <dd>{`${t(shippingPricingTerm.termType)} ${getCreateFlowAgreementTypeDisplay(shippingPricingTerm)}`}</dd>
          </div>
          {this.getDestinationCountryDescription(shippingPricingTerm)}
          {createFlowIsShippingPriceTermType(shippingPricingTerm.termType) && (
            <div>
              <dt>{t('common:commonTerminology.shippingPriceModel')}</dt>
              <dd>{shippingPricingTerm.shippingPriceModel.name}</dd>
            </div>
          )}
        </div>
      );

    // TODO: Think of a better way of disabling button than copying over conditions from different steps
    const conditions = [
      {
        predicate: !!initiator,
        text: t('createContract:getStarted.missingSellerMessage')
      },
      {
        predicate: acceptors && acceptors.length > 0,
        text: t('createContract:getStarted.missingBuyerMessage')
      },
      {
        predicate: !shouldHaveGracePeriod || (shouldHaveGracePeriod && gracePeriodApprovalDays > 0),
        text: t('createContract:getStarted.invalidGracePeriodSelection')
      },
      {
        predicate: Object.keys(selectedSkus).length > 0,
        text: t('createContract:addProducts.mustSelectProductsMessage')
      },
      {
        predicate:
          (!isEmpty(productPricingTerm) && productPricingTerm.termType) ||
          (!isEmpty(shippingPricingTerm) && shippingPricingTerm.termType),
        text: t('createContract:review.noContractBeingSetupMessage')
      }
    ];

    const [validCondition, invalidConditionReason] = evaluateCondition(conditions, false);

    return (
      <div>
        <StepTitle
          icon="finalize-contract-l"
          title={t('createContract:review.header', { count: acceptors.length })}
          description={t('createContract:review.description', { count: acceptors.length })}
        />
        <div>
          <div className="row">
            <div className={`col-sm-12 col-md-6 ${section}`}>
              <h4 className={sectionHeaderStyle}>
                {t('createContract:review.contractDetailsLabel', { count: acceptors.length })}
              </h4>

              {acceptors.map((o, idx) => (
                <Accordion
                  key={idx}
                  title={<span>{t('createContract:review.buyerHeader', { buyer: o.label })}</span>}
                  children={
                    <dl className={`dl-horizontal ${dlStyle}`}>
                      <div className={tableSpacing}>
                        <header className={tableHeaderStyle}>
                          <h5>{t('createContract:review.general')}</h5>
                        </header>
                        <dt>{t('common:commonTerminology.seller')}</dt>
                        <dd>{initiator.name}</dd>
                        <dt>{t('common:commonTerminology.buyer')}</dt>
                        <dd>{o.label}</dd>
                        <dt>{t('common:commonTerminology.hasGracePeriod')}</dt>
                        <dd>
                          {shouldHaveGracePeriod ? t('common:commonWords.yesText') : t('common:commonWords.noText')}
                        </dd>
                        {shouldHaveGracePeriod && (
                          <div>
                            {' '}
                            <dt>{t('common:commonTerminology.gracePeriod')}</dt>
                            <dd>
                              {t('contractInfo:gracePeriod', {
                                duration: moment.duration({ days: gracePeriodApprovalDays })
                              })}
                            </dd>{' '}
                          </div>
                        )}
                      </div>
                      {pricingTermsTable}
                      {shippingTermsTable}
                    </dl>
                  }
                  defaultOpen={idx === 0}
                />
              ))}
            </div>
            <div className={`col-sm-12 col-md-6 ${section}`}>
              <h4 className={sectionHeaderStyle}>
                {t('createContract:review.productsInContract', { count: acceptors.length })}
              </h4>
              <div>{selectedSkuCards}</div>
            </div>
          </div>
        </div>
        <WizardNavigator
          onPrev={this.props.onPrev}
          onNext={this.onNext}
          customNext={
            <Button
              className="btn btn-primary"
              onClick={this.onNext}
              disabled={!validCondition}
              disabledTooltipContents={invalidConditionReason.join('\n')}>
              {t('contractActions:sendForApproval')}
            </Button>
          }
        />
      </div>
    );
  }
}

export default withWizard(withTranslation()(ReviewAndFinalizeStep));
